import { TileCoordinates, TileRectangle } from "../../lib/runescape/coordinates";
import { TileArea } from "../../lib/runescape/coordinates/TileArea";
import { Rectangle, Vector2 } from "../../lib/math";
import { rangeRight } from "lodash";
import assert from "assert";
import { util } from "../../lib/util/util";
export var Scans;
(function (Scans) {
    var activate = TileArea.activate;
    function range(clue, meerkats) {
        return clue.range + (meerkats ? 5 : 0);
    }
    Scans.range = range;
    function get_pulse(spot, tile, range) {
        let d = distance(spot, tile);
        let p = 3 - Math.min(2, Math.floor(Math.max(0, (d - 1)) / range));
        assert(p >= 1 && p <= 3);
        let different_level = spot.level != tile.level || distance(complementSpot(spot), tile) <= range + 15;
        return {
            pulse: p,
            different_level: different_level
        };
    }
    Scans.get_pulse = get_pulse;
    function distance(player_tile, dig_tile) {
        return Vector2.max_axis(Vector2.sub(player_tile, dig_tile));
    }
    Scans.distance = distance;
    let Pulse;
    (function (Pulse) {
        var natural_order = util.Order.natural_order;
        var Order = util.Order;
        function hash(pulse) {
            return (pulse.pulse - 1) + (pulse.different_level ? 3 : 0);
        }
        Pulse.hash = hash;
        function unhash(hash) {
            return {
                pulse: (hash % 3) + 1,
                different_level: hash >= 3
            };
        }
        Pulse.unhash = unhash;
        function equals(a, b) {
            return a.pulse == b.pulse && a.different_level == b.different_level;
        }
        Pulse.equals = equals;
        Pulse.all = [
            // CAREFUL: This is sorted by the hash of the pulse (0 to 5), and MUST stay this way to not break some optimized code!
            { pulse: 1, different_level: false },
            { pulse: 2, different_level: false },
            { pulse: 3, different_level: false },
            { pulse: 1, different_level: true },
            { pulse: 2, different_level: true },
            { pulse: 3, different_level: true },
        ];
        function meta(type) {
            let pretty = ["Single", "Double", "Triple"][type.pulse - 1];
            // TODO: Clean this pos up
            if (type.different_level) {
                return {
                    pretty: type.pulse == 1 ? "Different Level" : pretty + " (DL)",
                    short: type.pulse == 1 ? "DL" : "DL" + type.pulse,
                    shorted: type.pulse == 1 ? "\"DL\"" : "\"DL\"" + type.pulse
                };
            }
            else {
                return {
                    pretty: pretty,
                    short: type.pulse.toString(),
                    shorted: type.pulse.toString()
                };
            }
        }
        Pulse.meta = meta;
        function simplify_with_context(pulse, context) {
            // Use the full word when it's not "different level"
            if (!pulse.different_level) {
                if (util.count(context, (p => p.different_level)) == context.length - 1)
                    return { type: null, text: "TF" }; // Is the only non-different level
                else
                    return { type: pulse.pulse, text: null };
            }
            else {
                let counterpart_exists = context.some(p => p.pulse == pulse.pulse && !p.different_level);
                if (!counterpart_exists)
                    return { type: pulse.pulse, text: null }; // If the non-different level counterpart does not exist, just use the pretty string
                if (util.count(context, (p => p.different_level)) == 1)
                    return { type: null, text: "DL" }; // Is the only different level
                else
                    return { type: pulse.pulse, text: "DL" };
            }
        }
        Pulse.simplify_with_context = simplify_with_context;
        function pretty_with_context(pulse, context) {
            let { type, text } = simplify_with_context(pulse, context);
            if (type == null) {
                switch (text) {
                    case "DL":
                        return "Different level";
                    case "TF":
                        return "Too far";
                    case null:
                        return "NULL";
                }
            }
            else {
                let pretty = ["Single", "Double", "Triple"][type - 1];
                switch (text) {
                    case "DL":
                        return `Different Level (${pretty})`;
                    case "TF":
                        return "NULL";
                    case null:
                        return pretty;
                }
            }
        }
        Pulse.pretty_with_context = pretty_with_context;
        function compare(a, b) {
            return natural_order(hash(a), hash(b));
        }
        Pulse.compare = compare;
        Pulse.comp = Order.comap(natural_order, hash);
    })(Pulse = Scans.Pulse || (Scans.Pulse = {}));
    function complementSpot(spot) {
        return {
            x: spot.x,
            y: (spot.y + 6400) % 12800,
            level: spot.level
        };
    }
    Scans.complementSpot = complementSpot;
    let PulseInformation;
    (function (PulseInformation) {
        function equals(a, b) {
            return Pulse.equals(a, b) && !(a.pulse == 3 && (b.pulse == 3) && !TileCoordinates.eq2(a?.spot, b.spot));
        }
        PulseInformation.equals = equals;
        function toString(self) {
            return self.pulse.toString()
                + (self.different_level ? "l" : "")
                + (self.pulse == 3 && self.spot ? TileCoordinates.toShortString(self.spot) : "");
        }
        PulseInformation.toString = toString;
    })(PulseInformation = Scans.PulseInformation || (Scans.PulseInformation = {}));
    function spot_narrowing(candidates, area, range) {
        return Pulse.all.flatMap((p) => {
            let remaining = narrow_down(candidates, { area: area, pulse: p.pulse, different_level: p.different_level }, range);
            if (p.pulse == 3) {
                return remaining.map(r => {
                    return {
                        pulse: {
                            pulse: 3,
                            different_level: p.different_level,
                            spot: r
                        },
                        narrowed_candidates: [r]
                    };
                });
            }
            else {
                return [{
                        pulse: p,
                        narrowed_candidates: remaining
                    }];
            }
        });
    }
    Scans.spot_narrowing = spot_narrowing;
    function area_pulse(spot, area, range) {
        if (!area.data) {
            // Optimized branch for rectangular areas.
            const rectArea = TileArea.toRect(area);
            let pulses;
            let max = get_pulse(spot, TileRectangle.clampInto(spot, rectArea), range).pulse;
            // This breaks if areas are so large they cover both cases. But in that case: Wtf are you doing?
            if (max == 1) {
                pulses = [];
                let complement_spot = complementSpot(spot);
                if (spot.level != rectArea.level || distance(complement_spot, Rectangle.clampInto(complement_spot, rectArea)) <= (range + 15)) {
                    // Any tile in area triggers different level
                    pulses.push({
                        pulse: 1,
                        different_level: true
                    });
                }
                if ((distance(complement_spot, rectArea.topleft) > (range + 15)
                    || distance(complement_spot, rectArea.botright) > (range + 15))
                    && spot.level == rectArea.level) { // Any tile in area does not trigger different level
                    pulses.push({
                        pulse: 1,
                        different_level: false
                    });
                }
            }
            else {
                let min = Math.min(get_pulse(spot, TileRectangle.tl(rectArea), range).pulse, get_pulse(spot, TileRectangle.br(rectArea), range).pulse);
                pulses = rangeRight(min, max + 1, 1).map((p) => {
                    return {
                        pulse: p,
                        different_level: spot.level != rectArea.level
                    };
                });
            }
            return pulses;
        }
        const pulse_hashes = [];
        activate(area).getTiles().forEach(t => {
            pulse_hashes.push(Pulse.hash(get_pulse(spot, t, range)));
        });
        return [...new Set(pulse_hashes).values()].map(Pulse.unhash);
    }
    Scans.area_pulse = area_pulse;
    function narrow_down(candidates, information, range) {
        return candidates.filter((s) => area_pulse(s, information.area, range).some((p2) => Pulse.equals(information, p2)));
    }
    Scans.narrow_down = narrow_down;
})(Scans || (Scans = {}));
